import React from "react";
import {
  Box,
  Space,
  PagingIndicator,
  LingoError,
  ActivityIndicator,
  Portal,
  EmptyState,
  Item,
  AnyObject,
} from "@thenounproject/lingo-core";

import SearchResultComponent from "./SearchResult";
import { SearchResult } from "@actions/search/createSearchHook";

function renderError(error: LingoError) {
  return (
    <EmptyState
      iconProps={{ iconId: "info.error", fill: "error", mt: "none", size: "60" }}
      title={error.message}
      size="small"
    />
  );
}
function renderEmptyState() {
  return (
    <EmptyState
      title="No results"
      subtitle="Try using broader search terms, checking for typos or changing your filters."
      size="small"
      iconProps={{
        iconId: "navigation.search",
        fill: "grayDark",
        mt: "none",
        size: "60",
      }}
    />
  );
}

type Props = {
  results: SearchResult;
  space: Space;
  portal: Portal;
  isLoading: boolean;
  fetchNextPage: () => void;
  error: null | LingoError;
};

export const AssetResultList = ({
  results,
  space,
  portal,
  isLoading,
  fetchNextPage,
  error,
}: Props) => {
  const hasMorePages = results?.results?.length < results?.total;

  if (error) return renderError(error);
  if (isLoading && !results?.results.length) return <ActivityIndicator center />;
  if (!results?.results?.length) return renderEmptyState();
  return (
    <Box pb="m" style={{ overflowY: "auto" }} data-clarity-mask="true">
      {results?.results?.map(({ object }) => (
        <SearchResultComponent
          key={(object as Item).id}
          item={object as Item}
          space={space}
          portal={portal}
        />
      ))}
      {hasMorePages && (
        <PagingIndicator
          data={results?.results}
          isLoading={isLoading}
          hasMore={hasMorePages}
          fetchNextPage={fetchNextPage}
          error={error}
        />
      )}
    </Box>
  );
};

export const JumpToResultList = ({
  results,
  space,
  portal,
  isLoading,
  fetchNextPage,
  error,
}: Props) => {
  const hasMorePages = results?.offset + results?.limit < results?.total;
  if (error) return renderError(error);
  if (!results?.results?.length) return renderEmptyState();

  const typeMap = {
    gallery: "item",
  };
  return (
    <Box pb="m" style={{ overflowY: "auto" }} data-clarity-mask="true">
      {results?.results?.map(({ object, type }) => {
        const key = typeMap[type] ?? type;
        const passProps = { [key]: object };
        return (
          <SearchResultComponent
            key={(object as AnyObject).id || (object as AnyObject).kitId}
            {...passProps}
            space={space}
            portal={portal}
          />
        );
      })}
      {hasMorePages && (
        <PagingIndicator
          data={results?.results}
          isLoading={isLoading}
          hasMore={hasMorePages}
          fetchNextPage={fetchNextPage}
          error={error}
        />
      )}
    </Box>
  );
};
